import { useMemo } from 'react';
import { Button, Checkbox, InputNumber, Select, Space, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';

import { EVERY_MAX, SCHEDULE_UNIT_OPTIONS, unitIsTimed } from '../../utils/automationSchedule';
import { TIME_FORMAT_PATTERNS } from '../../utils/dateDisplay';
import useTimeFormat from '../../hooks/useTimeFormat';
import { CLOSE, PLUS } from '../icons';

/**
 * The cadence controls every schedule-triggered rule kind needs: quantity +
 * unit, the time (or TIMES) of day, weekdays, day of month, timezone.
 *
 * Shared rather than copied because there are two scheduled kinds now — the
 * expected-item check and the generate-items generator — and a third is a
 * matter of time. A second copy of "every 2 weeks on Monday and Friday at
 * 08:00 in this zone" would drift from this one within a release, and the
 * fields it would drift on (the anchor the server owns, the canonical 24h
 * storage format, the per-unit ceilings) are exactly the ones that break
 * quietly.
 *
 * Controlled: `value` is the canonical schedule shape MINUS the anchor
 * (`{every, unit, time | times, daysOfWeek, dayOfMonth, timezone}`), and every
 * edit calls `onChange` with the whole next shape. `anchor` is never emitted —
 * the API stamps it, and a client echoing a stale one back would silently
 * re-phase somebody's rule.
 *
 * `refusedUnits` drops units a caller's API refuses (a stored export refuses
 * `minute` — 400 schedule_too_frequent — because a render every few minutes
 * is a loop, not a report). A stored value in a refused unit still shows, so
 * nothing is silently rewritten; the caller's validation says why it cannot
 * be saved.
 *
 * `allowMultipleTimes` switches the time control from one picker to a list.
 * One time is spelled `time` and several are spelled `times`, in both
 * directions, exactly as the API normalizes them — so a rule authored before
 * `times` existed round-trips byte-identical and its cadence never reads as
 * changed.
 */

const { Text } = Typography;

const WEEKDAY_OPTIONS = [
	{ label: 'Sun', value: 0 },
	{ label: 'Mon', value: 1 },
	{ label: 'Tue', value: 2 },
	{ label: 'Wed', value: 3 },
	{ label: 'Thu', value: 4 },
	{ label: 'Fri', value: 5 },
	{ label: 'Sat', value: 6 },
];

// What the API stores. The picker's DISPLAY format follows the user's clock
// preference; this one never moves.
const STORED_TIME_FORMAT = 'HH:mm';

// The API's own ceiling: a day has room for a slot every half hour and then
// some, and past that it is an interval cadence, which has its own unit.
export const MAX_TIMES = 24;

// Enough of a spread to be usable where Intl can't enumerate zones (older
// Safari, some jsdom builds). The browser's own zone is unioned in besides.
const FALLBACK_TIMEZONES = [
	'UTC',
	'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
	'America/Anchorage', 'Pacific/Honolulu', 'America/Sao_Paulo',
	'Europe/London', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Moscow',
	'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
	'Australia/Sydney', 'Pacific/Auckland',
];

export const browserTimezone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	}
	catch {
		return 'UTC';
	}
};

export const supportedTimezones = () => {
	try {
		if ( typeof Intl.supportedValuesOf === 'function' ) {
			const zones = Intl.supportedValuesOf( 'timeZone' );
			if ( Array.isArray( zones ) && zones.length ) return zones;
		}
	}
	catch {
		// Fall through to the static list.
	}
	return FALLBACK_TIMEZONES;
};

// One zone, several names. Browsers disagree on which they list — Chrome
// (ICU) lists Europe/Kiev and Asia/Calcutta, Firefox Europe/Kyiv and
// Asia/Kolkata — and people know the US/* links. Each group's names are
// search words for every other, so either spelling finds whichever the
// browser offers. Search words only: a pick stores the name picked.
const ZONE_ALIASES = [
	[ 'Europe/Kyiv', 'Europe/Kiev' ],
	[ 'Asia/Kolkata', 'Asia/Calcutta' ],
	[ 'Asia/Ho_Chi_Minh', 'Asia/Saigon' ],
	[ 'America/Nuuk', 'America/Godthab' ],
	[ 'Asia/Yangon', 'Asia/Rangoon' ],
	[ 'Asia/Kathmandu', 'Asia/Katmandu' ],
	[ 'Atlantic/Faroe', 'Atlantic/Faeroe' ],
	[ 'Africa/Asmara', 'Africa/Asmera' ],
	[ 'Pacific/Kanton', 'Pacific/Enderbury' ],
	[ 'America/Argentina/Buenos_Aires', 'America/Buenos_Aires' ],
	[ 'America/Indiana/Indianapolis', 'America/Indianapolis', 'US/East-Indiana' ],
	[ 'America/Los_Angeles', 'US/Pacific' ],
	[ 'America/Denver', 'US/Mountain' ],
	[ 'America/Chicago', 'US/Central' ],
	[ 'America/New_York', 'US/Eastern' ],
	[ 'America/Anchorage', 'US/Alaska' ],
	[ 'Pacific/Honolulu', 'US/Hawaii' ],
	[ 'America/Phoenix', 'US/Arizona' ],
	[ 'America/Adak', 'US/Aleutian' ],
	[ 'America/Detroit', 'US/Michigan' ],
	[ 'America/Indiana/Knox', 'America/Knox_IN', 'US/Indiana-Starke' ],
	[ 'Pacific/Pago_Pago', 'US/Samoa' ],
	[ 'UTC', 'Etc/UTC', 'GMT' ],
];
const ALIAS_WORDS = new Map();
ZONE_ALIASES.forEach( names => names.forEach( name => {
	ALIAS_WORDS.set( name, names.filter( other => other !== name ) );
} ) );

const wordsFor = zone => [ zone, zone.replace( /[/_]/g, ' ' ) ];

/**
 * The zone picker's options — shared by the schedule editor and a
 * collection's Time zone setting, so both offer the same list: every zone
 * this browser can name, bare `UTC` (Intl.supportedValuesOf only lists Etc/*,
 * and a manager should not need to know Etc/GMT means UTC), the browser's own
 * zone, and each zone in `current` — a value always has to be selectable, even
 * one stored from a browser that names zones differently. `searchText` lets
 * "los angeles" find `America/Los_Angeles`, and "kyiv" or "US/Pacific" find
 * whichever spelling the browser lists, through `optionSearchFilter`.
 */
export const timezoneOptions = ( ...current ) => {
	const zones = new Set( supportedTimezones() );
	zones.add( browserTimezone() );
	zones.add( 'UTC' );
	current.forEach( zone => { if ( typeof zone === 'string' && zone ) zones.add( zone ); } );
	return [ ...zones ].sort().map( zone => ( {
		value: zone,
		label: zone,
		searchText: [ ...wordsFor( zone ), ...( ALIAS_WORDS.get( zone ) || [] ).flatMap( wordsFor ) ].join( ' ' ),
	} ) );
};

/** Stored `HH:mm` -> a dayjs the picker can render, or null. Parsed by hand:
 * customParseFormat is not installed app-wide, and a stored time is only ever
 * `HH:mm`. */
export const parseStoredTime = ( time ) => {
	const match = /^(\d{1,2}):(\d{2})$/.exec( `${ time || '' }`.trim() );
	if ( ! match ) return null;
	const hours = Number( match[ 1 ] );
	const minutes = Number( match[ 2 ] );
	if ( hours > 23 || minutes > 59 ) return null;
	return dayjs().hour( hours ).minute( minutes ).second( 0 ).millisecond( 0 );
};

/**
 * The slots a schedule carries, always as a list — `times` when it has one,
 * otherwise the single `time`, otherwise the platform default.
 */
export const timesOf = ( schedule ) => {
	if ( Array.isArray( schedule?.times ) && schedule.times.length ) return schedule.times;
	return [ schedule?.time || '09:00' ];
};

/**
 * A list of slots back into the canonical spelling: one is `time`, several are
 * a sorted, de-duplicated `times`.
 */
export const timesToSchedule = ( list ) => {
	const clean = Array.from( new Set( ( list || [] ).map( t => `${ t || '' }`.trim() ).filter( Boolean ) ) )
		.sort()
		.slice( 0, MAX_TIMES );
	if ( clean.length <= 1 ) return { time: clean[ 0 ] || '09:00' };
	return { times: clean };
};

export default function ScheduleFields( {
	value,
	onChange,
	idPrefix = 'schedule',
	allowMultipleTimes = false,
	everyLabel = 'Every',
	timeLabel = 'At',
	timezoneHint = 'Every time above is read in this zone.',
	refusedUnits = null,
} ) {

	const timeFormat = useTimeFormat();
	const timePattern = TIME_FORMAT_PATTERNS[ timeFormat ] || TIME_FORMAT_PATTERNS[ '12h' ];

	const schedule = value || {};
	const unit = schedule.unit || 'day';
	const every = Number.isFinite( Number( schedule.every ) ) && Number( schedule.every ) > 0 ? Number( schedule.every ) : 1;
	const timezone = schedule.timezone || browserTimezone();
	const daysOfWeek = Array.isArray( schedule.daysOfWeek ) ? schedule.daysOfWeek : [];
	const times = timesOf( schedule );

	const unitOptions = useMemo( () => {
		if ( ! Array.isArray( refusedUnits ) || ! refusedUnits.length ) return SCHEDULE_UNIT_OPTIONS;
		return SCHEDULE_UNIT_OPTIONS.filter( option => option.value === unit || ! refusedUnits.includes( option.value ) );
	}, [ refusedUnits, unit ] );

	const patch = next => onChange( { ...schedule, ...next } );
	const setTimes = list => onChange( {
		...schedule,
		time: undefined,
		times: undefined,
		...timesToSchedule( list ),
	} );

	// The current value always has to be selectable, even when it came from a
	// rule authored in a zone this browser cannot enumerate.
	const zoneOptions = useMemo( () => timezoneOptions( timezone ), [ timezone ] );

	return (<>
		<div>
			<Space align='start'>
				<div>
					<label htmlFor={`${ idPrefix }Every`} className='d-block'>{everyLabel}</label>
					<InputNumber
						id={`${ idPrefix }Every`}
						inputMode='numeric'
						min={1}
						max={EVERY_MAX[ unit ] || 365}
						value={every}
						onChange={next => patch( { every: next } )}
					/>
				</div>
				<div>
					<label htmlFor={`${ idPrefix }Unit`} className='d-block'>Unit</label>
					<Select
						id={`${ idPrefix }Unit`}
						style={{ width: 140 }}
						value={unit}
						options={unitOptions}
						onChange={next => patch( { unit: next } )}
					/>
				</div>
			</Space>
		</div>

		{unitIsTimed( unit ) ? (
			<div>
				<label htmlFor={`${ idPrefix }Time0`} className='d-block'>{timeLabel}</label>
				<Space direction='vertical' size={4} style={{ width: '100%' }}>
					{( allowMultipleTimes ? times : times.slice( 0, 1 ) ).map( ( slot, index ) => (
						// Index-keyed on purpose: the list is a set of times with
						// no identity of their own, and it re-sorts on every edit.
						<Space key={`slot-${ index }`} align='center'>
							<TimePicker
								id={`${ idPrefix }Time${ index }`}
								style={{ width: 140 }}
								// The PICKER speaks the user's clock; the SCHEDULE is
								// always canonical 24h HH:mm, which is what the API
								// stores and what every window calculation reads.
								format={timePattern}
								needConfirm={false}
								allowClear={false}
								value={parseStoredTime( slot )}
								onChange={next => {
									const list = [ ...times ];
									list[ index ] = next ? dayjs( next ).format( STORED_TIME_FORMAT ) : '';
									setTimes( list );
								}}
							/>
							{allowMultipleTimes && times.length > 1 ? (
								<Button
									type='text'
									danger
									icon={<CLOSE />}
									aria-label={`Remove the ${ slot } slot`}
									onClick={() => setTimes( times.filter( ( t, i ) => i !== index ) )}
								/>
							) : null}
						</Space>
					) )}
					{allowMultipleTimes && times.length < MAX_TIMES ? (
						<Button
							size='small'
							type='dashed'
							icon={<PLUS />}
							onClick={() => setTimes( [ ...times, '' ] )}
						>
							Add a time
						</Button>
					) : null}
				</Space>
				<Text type='secondary' className='d-block'>
					{timeFormat === '24h' ? '24-hour clock' : '12-hour clock'} — change it in Preferences.
					{allowMultipleTimes ? ' Several times a day is one rule, not several.' : ''}
				</Text>
			</div>
		) : null}

		{unit === 'week' ? (
			<div>
				<Text strong className='d-block'>On</Text>
				<Checkbox.Group
					options={WEEKDAY_OPTIONS}
					value={daysOfWeek}
					onChange={next => patch( { daysOfWeek: next } )}
				/>
			</div>
		) : null}

		{unit === 'month' || unit === 'year' ? (
			<div>
				<label htmlFor={`${ idPrefix }DayOfMonth`} className='d-block'>Day of the month</label>
				<InputNumber
					id={`${ idPrefix }DayOfMonth`}
					inputMode='numeric'
					min={1}
					max={31}
					value={schedule.dayOfMonth ?? null}
					placeholder={unit === 'year' ? 'Any day' : '1'}
					onChange={next => patch( { dayOfMonth: next } )}
				/>
				<Text type='secondary' className='d-block'>
					{unit === 'year'
						? 'Optional — leave empty to keep the anniversary day.'
						: 'Past the end of a short month, it fires on the last day.'}
				</Text>
			</div>
		) : null}

		<div>
			<label htmlFor={`${ idPrefix }Timezone`} className='d-block'>Timezone</label>
			<Select
				id={`${ idPrefix }Timezone`}
				showSearch
				style={{ width: '100%' }}
				value={timezone}
				options={zoneOptions}
				onChange={next => patch( { timezone: next } )}
			/>
			<Text type='secondary'>{timezoneHint}</Text>
		</div>
	</>);
}
